/*
 * ******************************************************************
 * Copyright (c) 2021-2026 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */

import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import { VLR_CONSOLE } from "@vlr-console/vlr-console-routing-util";
import { OPERATOR_CONSOLE } from "@op-console/op-console-routing-util";
import { VMCFS_CONSOLE } from "@vmcfs-console/vmcfs-console-routing-util";
import { ConsoleType } from "@shared/models/global.model";
import { AppConfigService, AUTH_KEYS, RbacService } from "@app/services";
import { UiPrivilegeEnum } from "@shared/services/rbac/roles.model";

// @formatter:off
@Injectable({
    providedIn: "root"
})
// @formatter:on
export class RoleGuard implements CanActivate, CanActivateChild {

    constructor(private router: Router,
                private rbacService: RbacService,
                private appConfig: AppConfigService) {
    }

    canActivate(route: ActivatedRouteSnapshot,
                state: RouterStateSnapshot): boolean | UrlTree {

        const url: string = state.url;

        // If the client type is operator, redirect to the operator console.
        if (this.appConfig.clientType === ConsoleType.OPERATOR) {
            // Valid Operator console UI path root.
            if (url.startsWith(OPERATOR_CONSOLE.ROOT_ABSOLUTE)) {
                return true;
            }

            // Root path or unknown child path.
            return this.router.parseUrl(OPERATOR_CONSOLE.ROOT);
        }

        // VCDR console UI pathname - restrict routes as necessary
        if (url.startsWith(VLR_CONSOLE.ROOT_ABSOLUTE)) {
            // Restrict route to main dashboard view.
            if (url.startsWith(VLR_CONSOLE.DEPLOYMENTS_ABSOLUTE) && url !== VLR_CONSOLE.DEPLOYMENTS_ABSOLUTE &&
                url !== VLR_CONSOLE.ONPREM_IRE_SETUP_ABSOLUTE) {
                return this.router.parseUrl(VLR_CONSOLE.DEPLOYMENTS_ABSOLUTE);
            }
            // Restrict route to main subscriptions view.
            if (url.startsWith(VLR_CONSOLE.SUBSCRIPTIONS_ABSOLUTE) && url !== VLR_CONSOLE.SUBSCRIPTIONS_ABSOLUTE) {
                return this.router.parseUrl(VLR_CONSOLE.SUBSCRIPTIONS_ABSOLUTE);
            }
            return true;
        }

        // Storage console UI pathname - restrict routes as necessary
        if (url.startsWith(VMCFS_CONSOLE.ROOT_ABSOLUTE)) {
            // Restrict route to main regions view.
            if (url.startsWith(VMCFS_CONSOLE.REGIONS_ABSOLUTE) && url !== VMCFS_CONSOLE.REGIONS_ABSOLUTE) {
                return this.router.parseUrl(VMCFS_CONSOLE.REGIONS_ABSOLUTE);
            }
            // Restrict route to main subscriptions view.
            if (url.startsWith(VMCFS_CONSOLE.SUBSCRIPTIONS_ABSOLUTE) &&
                url !== VMCFS_CONSOLE.SUBSCRIPTIONS_ABSOLUTE) {
                return this.router.parseUrl(VMCFS_CONSOLE.SUBSCRIPTIONS_ABSOLUTE);
            }
            // Restrict route to tasks view.
            if (url.startsWith(VMCFS_CONSOLE.ACTIVITY_LOG_ABSOLUTE) && url !== VMCFS_CONSOLE.ACTIVITY_LOG_ABSOLUTE) {
                return this.router.parseUrl(VMCFS_CONSOLE.ACTIVITY_LOG_ABSOLUTE);
            }
            return true;
        }

        // Root path ("/") or unknown path. Check the client ID to determine where to redirect.
        const clientType: ConsoleType = <ConsoleType>window.sessionStorage.getItem(AUTH_KEYS.CLIENT_TYPE);
        if (clientType === ConsoleType.VMCFS) {
            // Previous client was storage. Redirect to the storage console UI.
            return this.router.parseUrl(VMCFS_CONSOLE.ROOT);
        }
        // All other client types redirect to VCDR console root.
        return this.router.parseUrl(VLR_CONSOLE.ROOT);
    }

    canActivateChild(route: ActivatedRouteSnapshot,
                     state: RouterStateSnapshot): boolean | UrlTree {
        const url: string = state.url;

        // Operator console UI user privilege check.
        if (this.rbacService.hasOperatorRole()) {
            // All routes are visible with any subset of the current Operator Console UI privileges.
            return true;
        }

        // VCDR console UI user privilege check.
        if (url.startsWith(VLR_CONSOLE.ROOT_ABSOLUTE)) {
            if (this.rbacService.hasVlrRole()) {
                // Dashboard route privilege check.
                if (url.startsWith(VLR_CONSOLE.DEPLOYMENTS_ABSOLUTE)) {
                    if (this.rbacService.hasPrivilege(UiPrivilegeEnum.VIEW_DEPLOYMENTS)) {
                        return true;
                    }
                    return this.router.parseUrl(VLR_CONSOLE.SUBSCRIPTIONS_ABSOLUTE);
                }

                if (url.startsWith(VLR_CONSOLE.REGIONS_SETUP_ABSOLUTE) ||
                    url.startsWith(VLR_CONSOLE.REGIONS_IMPORT_ABSOLUTE)) {
                    // TODO: Add privilege checks
                    return true;
                }

                // Subscriptions route privilege check.
                if (url.startsWith(VLR_CONSOLE.SUBSCRIPTIONS_ABSOLUTE)) {
                    return true;
                }

                // Usage charts route privilege check.
                if (url.startsWith(VLR_CONSOLE.SUBSCRIPTION_USAGE_VLR_ABSOLUTE) ||
                    url.startsWith(VLR_CONSOLE.SUBSCRIPTION_USAGE_VMC_ABSOLUTE)) {
                    if (this.rbacService.hasPrivilege(UiPrivilegeEnum.VIEW_DEPLOYMENTS)) {
                        return true;
                    }
                    return this.router.parseUrl(VLR_CONSOLE.SUBSCRIPTIONS_ABSOLUTE);
                }

                // Tasks & Events route privilege check.
                if (url.startsWith(VLR_CONSOLE.ACTIVITY_LOG_ABSOLUTE)) {
                    if (this.rbacService.hasPrivilege(UiPrivilegeEnum.VIEW_ACTIVITY_LOG)) {
                        return true;
                    }
                }

                // Settings route privilege check.
                if (url.startsWith(VLR_CONSOLE.SETTINGS_ABSOLUTE)) {
                    if (this.rbacService.hasPrivilege(UiPrivilegeEnum.VIEW_SETTINGS)) {
                        return true;
                    }
                }

                // Has a VLR role, but no privileges for any route.
                return this.router.parseUrl(VLR_CONSOLE.ERROR_403);
            }
        }

        // Storage console UI user privilege check.
        if (url.startsWith(VMCFS_CONSOLE.ROOT_ABSOLUTE) && this.rbacService.hasVmcfsConsoleRole()) {

            // Regions route privilege check.
            if (url.startsWith(VMCFS_CONSOLE.REGIONS_ABSOLUTE)) {
                if (this.rbacService.hasPrivilege(UiPrivilegeEnum.VIEW_DEPLOYMENTS)) {
                    return true;
                }
                return this.router.parseUrl(VMCFS_CONSOLE.SUBSCRIPTIONS_ABSOLUTE);
            }

            // Subscriptions route privilege check.
            if (url.startsWith(VMCFS_CONSOLE.SUBSCRIPTIONS_ABSOLUTE)) {
                return true;
            }

            // Usage charts route privilege check.
            if (url.startsWith(VMCFS_CONSOLE.SUBSCRIPTION_REPORTS_ABSOLUTE)) {
                if (this.rbacService.hasPrivilege(UiPrivilegeEnum.VIEW_DEPLOYMENTS)) {
                    return true;
                }
                return this.router.parseUrl(VMCFS_CONSOLE.SUBSCRIPTIONS_ABSOLUTE);
            }

            // Tasks & Events route privilege check.
            if (url.startsWith(VMCFS_CONSOLE.ACTIVITY_LOG_ABSOLUTE)) {
                if (this.rbacService.hasPrivilege(UiPrivilegeEnum.VIEW_ACTIVITY_LOG)) {
                    return true;
                }
            }

            // Settings route privilege check.
            if (url.startsWith(VMCFS_CONSOLE.SETTINGS_ABSOLUTE)) {
                if (this.rbacService.hasPrivilege(UiPrivilegeEnum.VIEW_SETTINGS)) {
                    return true;
                }
            }

            // Has a storage UI role, but no privileges for any route.
            return this.router.parseUrl(VMCFS_CONSOLE.ERROR_403);
        }

        // No valid roles. Routes are allowed, but the views must handle this. This is per UX design.
        return true;
    }
}
